import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Inbox,
  Phone,
  Mail,
  Search,
  Loader2,
  RefreshCw,
  Image as ImageIcon,
  MessageSquareQuote,
  ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StoriesManager from "@/components/admin/StoriesManager";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "closed"];

const STATUS_STYLE = {
  new: "bg-orange-100 text-orange-700 border-orange-200",
  contacted: "bg-blue-100 text-blue-700 border-blue-200",
  closed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-block text-xs font-semibold capitalize px-3 py-1 rounded-full border ${STATUS_STYLE[status] || ""}`}>
      {status}
    </span>
  );
}

export default function Admin() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, authHeader);
      setQuotes(data);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const updateStatus = async (id, status) => {
    const prev = quotes;
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, authHeader);
      toast.success(`Marked as ${status}.`);
    } catch {
      setQuotes(prev);
      toast.error("Could not update status.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const counts = useMemo(() => {
    const c = { all: quotes.length, new: 0, contacted: 0, closed: 0 };
    quotes.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [quotes]);

  const visible = useMemo(() => {
    return quotes.filter((q) => {
      const matchStatus = filter === "all" || q.status === filter;
      const s = search.toLowerCase();
      const matchSearch =
        !s ||
        q.name.toLowerCase().includes(s) ||
        q.email.toLowerCase().includes(s) ||
        q.service_type.toLowerCase().includes(s);
      return matchStatus && matchSearch;
    });
  }, [quotes, filter, search]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            <img src={BRAND.logoDark} alt="Kairos Buildings" className="h-11 w-11 object-contain" />
            <div>
              <div className="font-heading font-bold leading-tight">Kairos Buildings</div>
              <div className="text-xs text-slate-400">Admin Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-slate-300">{user?.email}</span>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/15 hover:text-white"
            >
              <LogOut size={16} className="mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <Tabs defaultValue="quotes">
          <TabsList className="mb-8 bg-slate-100 p-1 rounded-full h-auto flex-wrap">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-full px-5 py-2 data-[state=active]:bg-white">
              <ClipboardList size={16} className="mr-2" /> Quotes
            </TabsTrigger>
            <TabsTrigger value="stories" data-testid="tab-stories" className="rounded-full px-5 py-2 data-[state=active]:bg-white">
              <MessageSquareQuote size={16} className="mr-2" /> Stories
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Quote Submissions</h1>
            <p className="text-slate-500 mt-1">Review and manage incoming project requests.</p>
          </div>
          <Button onClick={load} variant="outline" className="rounded-full self-start" data-testid="refresh-button">
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        </div>

        {/* Stat cards / filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { key: "all", label: "Total" },
            { key: "new", label: "New" },
            { key: "contacted", label: "Contacted" },
            { key: "closed", label: "Closed" },
          ].map((c) => (
            <button
              key={c.key}
              data-testid={`filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`text-left bg-white border rounded-2xl p-5 transition-all ${
                filter === c.key ? "border-blue-600 ring-2 ring-blue-100" : "border-slate-100 hover:border-slate-200"
              }`}
            >
              <div className="font-heading text-3xl font-bold text-slate-900">{counts[c.key] || 0}</div>
              <div className="text-sm text-slate-500 mt-1">{c.label}</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or service…"
            className="pl-10"
          />
        </div>

        {/* Table / list */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="animate-spin mr-2" /> Loading submissions…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-slate-100" data-testid="empty-state">
            <Inbox className="mx-auto text-slate-300" size={40} />
            <p className="text-slate-500 mt-3">No submissions found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((q) => (
              <div
                key={q.id}
                data-testid={`quote-row-${q.id}`}
                className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-lg font-semibold text-slate-900">{q.name}</h3>
                      <StatusBadge status={q.status} />
                      <span className="text-xs text-slate-400">{fmtDate(q.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-blue-700">
                        <Mail size={14} /> {q.email}
                      </a>
                      {q.phone && (
                        <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-blue-700">
                          <Phone size={14} /> {q.phone}
                        </a>
                      )}
                      <span className="inline-flex items-center gap-1.5 font-medium text-orange-600">
                        {q.service_type}
                      </span>
                    </div>
                    <p className="text-slate-600 mt-3 leading-relaxed">{q.message}</p>
                  </div>

                  <div className="shrink-0 w-full lg:w-48">
                    <label className="text-xs text-slate-400 mb-1 block">Update status</label>
                    <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                      <SelectTrigger data-testid={`status-select-${q.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`status-option-${s}`} className="capitalize">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="portfolio">
            <h1 className="font-heading text-3xl font-semibold text-slate-900 mb-1">Selected Work</h1>
            <PortfolioManager token={token} />
          </TabsContent>

          <TabsContent value="stories">
            <h1 className="font-heading text-3xl font-semibold text-slate-900 mb-1">Homeowner Stories</h1>
            <StoriesManager token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
